class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.vlInitRetener();
  }

  /* ===== Vestaluna: mensaje de retención al intentar cerrar el cajón =====
     Al cerrar (X, clic fuera, Escape o botón atrás del móvil) con productos
     dentro, la primera vez por sesión se enseña el bloque [data-vl-retener]
     y el cajón se queda abierto. «Seguir con mi pedido» oculta el bloque;
     «Cerrar» (o un segundo intento) cierra de verdad. El visto se guarda en
     sessionStorage; si sessionStorage falla, no se enseña nada y el cajón
     cierra con normalidad. Sin librerías externas. */
  vlInitRetener() {
    this.vlHistorial = false;
    this.addEventListener('click', (evt) => {
      if (evt.target.closest('[data-vl-retener-seguir]')) {
        evt.preventDefault();
        this.vlOcultarRetener();
        return;
      }
      if (evt.target.closest('[data-vl-retener-cerrar]')) {
        evt.preventDefault();
        this.vlCerrar();
      }
    });
    // Botón atrás del móvil: al abrir se apunta un estado en el historial y
    // al volver atrás se trata como un intento de cierre.
    window.addEventListener('popstate', () => {
      if (!this.classList.contains('active')) return;
      this.vlHistorial = false;
      this.close();
      if (this.classList.contains('active')) this.vlEmpujarHistorial();
    });
  }

  vlEmpujarHistorial() {
    if (window.Shopify && window.Shopify.designMode) return;
    try {
      history.pushState({ vlCajonCarrito: true }, '');
      this.vlHistorial = true;
    } catch (e) {
      this.vlHistorial = false;
    }
  }

  vlRetenerPendiente() {
    if (!this.querySelector('.cart-item')) return false;
    const bloque = this.querySelector('[data-vl-retener]');
    if (!bloque || !bloque.hidden) return false;
    try {
      if (window.sessionStorage.getItem('vl_retener_visto')) return false;
      window.sessionStorage.setItem('vl_retener_visto', '1');
    } catch (e) {
      return false;
    }
    // La frase del 10 % solo para quien ya tiene el código (se apuntó en el popup).
    const texto = bloque.querySelector('[data-vl-retener-texto]');
    try {
      if (texto && window.localStorage.getItem('vl_bienvenida') === 'apuntada') texto.textContent = texto.dataset.conCodigo;
    } catch (e) {}
    bloque.hidden = false;
    const boton = bloque.querySelector('[data-vl-retener-seguir]');
    if (boton) boton.focus();
    return true;
  }

  vlOcultarRetener() {
    const bloque = this.querySelector('[data-vl-retener]');
    if (bloque) bloque.hidden = true;
  }

  vlCerrar() {
    this.vlOcultarRetener();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    if (this.vlHistorial) {
      this.vlHistorial = false;
      try {
        history.back();
      } catch (e) {
        /* nada */
      }
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });
    this.vlEmpujarHistorial();

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    if (this.vlRetenerPendiente()) return;
    this.vlCerrar();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
